/**
 * Leads Service
 * Handles lead creation and retrieval using Supabase Edge Functions
 */

import { logger } from '../lib/utils';

export interface Lead {
  id?: number;
  session_id: string;
  order_number?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  gender?: string;
  country?: string;
  city?: string;
  postal_code?: string;
  street?: string;
  house_number?: string;
  birthdate?: string;
  known_info?: string;
  agrees_to_terms?: boolean;
  agrees_to_no_marketing?: boolean;
  payment_method?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  url_query_string?: string;
  referrer_url?: string;
  converted?: boolean;
  converted_at?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Get Supabase Edge Function URL
 */
const getEdgeFunctionUrl = (functionName: string): string => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not defined');
  }
  return `${supabaseUrl}/functions/v1/${functionName}`;
};

/**
 * Get authorization header
 */
const getAuthHeaders = (): HeadersInit => {
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!anonKey) {
    throw new Error('VITE_SUPABASE_ANON_KEY is not defined');
  }
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${anonKey}`,
  };
};

/**
 * Create a new lead
 */
export async function createLead(
  sessionId: string,
  email: string,
  formData: {
    gender?: string;
    firstName?: string;
    lastName?: string;
    country?: string;
    city?: string;
    postalCode?: string;
    street?: string;
    houseNumber?: string;
    birthdate?: string;
    knownInfo?: string;
    agreesToTerms?: boolean;
    agreesToNoMarketing?: boolean;
  }
): Promise<{ success: boolean; lead?: Lead; order_number?: string; error?: string }> {
  try {
    const response = await fetch(getEdgeFunctionUrl('create-lead'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        session_id: sessionId,
        email: email.toLowerCase().trim(),
        form_data: formData,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      const errorMessage = result.error || 'Failed to create lead';
      logger.error('Failed to create lead:', errorMessage);
      
      // Handle duplicate lead
      if (result.code === 'DUPLICATE_LEAD') {
        return {
          success: false,
          error: 'A lead with this email or session already exists',
        };
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }

    logger.log(`Lead created successfully with order number: ${result.order_number}`);
    
    return {
      success: true,
      order_number: result.order_number,
      lead: {
        session_id: sessionId,
        email: result.email,
        order_number: result.order_number,
        ...formData,
      },
    };

  } catch (error) {
    logger.error('Error creating lead:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get lead by session ID
 */
export async function getLeadBySessionId(sessionId: string): Promise<Lead | null> {
  try {
    const response = await fetch(
      `${getEdgeFunctionUrl('get-session-data')}?session_id=${sessionId}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Failed to get lead:', result.error);
      return null;
    }

    return result.lead;

  } catch (error) {
    logger.error('Error getting lead by session ID:', error);
    return null;
  }
}

/**
 * Mark lead as converted
 */
export async function markLeadAsConverted(
  sessionId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(getEdgeFunctionUrl('mark-lead-converted'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        session_id: sessionId,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      const errorMessage = result.error || 'Failed to mark lead as converted';
      logger.error('Failed to mark lead as converted:', errorMessage);
      
      if (result.code === 'LEAD_NOT_FOUND') {
        return {
          success: false,
          error: 'Lead not found',
        };
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }

    logger.log(`Lead marked as converted: ${result.order_number}`);
    
    return {
      success: true,
    };

  } catch (error) {
    logger.error('Error marking lead as converted:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Mark lead as converted by order number
 */
export async function markLeadAsConvertedByOrderNumber(
  orderNumber: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(getEdgeFunctionUrl('mark-lead-converted'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        order_number: orderNumber,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      const errorMessage = result.error || 'Failed to mark lead as converted';
      logger.error('Failed to mark lead as converted:', errorMessage);
      
      if (result.code === 'LEAD_NOT_FOUND') {
        return {
          success: false,
          error: 'Lead not found',
        };
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }

    logger.log(`Lead marked as converted: ${result.order_number}`);
    
    return {
      success: true,
    };

  } catch (error) {
    logger.error('Error marking lead as converted:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Update payment method for a lead
 */
export async function updateLeadPaymentMethod(
  sessionIdOrOrderNumber: string,
  paymentMethod: string,
  useOrderNumber: boolean = false
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(getEdgeFunctionUrl('update-lead-payment-method'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...(useOrderNumber 
          ? { order_number: sessionIdOrOrderNumber }
          : { session_id: sessionIdOrOrderNumber }
        ),
        payment_method: paymentMethod,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      const errorMessage = result.error || 'Failed to update payment method';
      logger.error('Failed to update payment method:', errorMessage);
      
      if (result.code === 'LEAD_NOT_FOUND') {
        return {
          success: false,
          error: 'Lead not found',
        };
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }

    logger.log(`Payment method updated successfully: ${result.payment_method}`);
    
    return {
      success: true,
    };

  } catch (error) {
    logger.error('Error updating payment method:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Update lead data (for when user changes selections)
 */
export async function updateLeadData(
  sessionId: string,
  data: {
    email?: string;
    knownInfo?: string;
    street?: string;
    houseNumber?: string;
    birthdate?: string;
    [key: string]: any;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(getEdgeFunctionUrl('update-lead-data'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        session_id: sessionId,
        data,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      const errorMessage = result.error || 'Failed to update lead data';
      logger.error('Failed to update lead data:', errorMessage);
      
      if (result.code === 'LEAD_NOT_FOUND') {
        return {
          success: false,
          error: 'Lead not found',
        };
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }

    logger.log(`Lead data updated successfully: ${result.updated_fields?.join(', ')}`);
    
    return {
      success: true,
    };

  } catch (error) {
    logger.error('Error updating lead data:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get auftragsnummer from leads table by session ID
 */
export async function getLeadAuftragsnummer(sessionId: string): Promise<string | null> {
  try {
    const response = await fetch(
      `${getEdgeFunctionUrl('get-lead-auftragsnummer')}?session_id=${sessionId}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error fetching auftragsnummer:', result.error);
      return null;
    }

    return result.auftragsnummer ?? null;
  } catch (error) {
    logger.error('Error fetching auftragsnummer:', error);
    return null;
  }
}

/**
 * Get the current time in Berlin timezone
 */
export function getBerlinTime() {
  return new Date().toLocaleString('en-US', { timeZone: 'Europe/Berlin' });
}
